/**
 * UsersWizardPage — route `/users/invite`.
 *
 * SCRUM-108 Phase 4: wires the wizard to the live
 * `POST /organizations/:slug/invitations` endpoint. The PR #134
 * "send-disabled" placeholder is gone.
 *
 *  - Identify step captures firstName / lastName / email / jobTitle.
 *    firstName + lastName + jobTitle are FE-only display fields today;
 *    the BE create-invitation body only accepts `email` + `role_id`.
 *    When the BE grows a profile-pre-fill API for invited users, the
 *    captured display fields plumb through.
 *  - Roles step renders the live `useRolesList` roles as a single-select
 *    radio picker (BE accepts one role_id per invitation).
 *  - Confirm step renders captured values + Send invite, which fires
 *    `useCreateInvitation` and navigates to `/users` on success.
 *  - 422 errors map to specific inline messages:
 *    - `pending_exists` → "this user already has a pending invitation"
 *    - `already_member` → "this user is already a member"
 *    - `self_invite` → "you can't invite yourself"
 *    - `invalid_email` → "email looks invalid"
 *    - `role_not_found` → "that role isn't available — reload"
 *  - 403 → permission-denied alert.
 *  - Generic errors → "couldn't send invite, try again".
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextField } from '@sparta/ui-kit';
import { SectionCard } from '../_components/SectionCard.js';
import { SectionHeading } from '../_components/SectionHeading.js';
import { WizardStepRail } from '../_components/WizardStepRail.js';
import { useRolesList } from '../api/use-roles-list.js';
import { useCreateInvitation } from '../api/use-create-invitation.js';
import { RequirePermission } from '../../../shared/auth/RequirePermission.js';

const STEPS = ['Identify', 'Roles', 'Confirm'] as const;

interface IdentifyForm {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
}

const EMPTY_IDENTIFY: IdentifyForm = {
  firstName: '',
  lastName: '',
  email: '',
  jobTitle: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function unprocessableMessage(reason: string): string {
  switch (reason) {
    case 'pending_exists':
      return 'This person already has a pending invitation. Open the existing one to resend instead.';
    case 'already_member':
      return 'This person is already a member of this organization.';
    case 'self_invite':
      return 'You can’t invite yourself.';
    case 'invalid_email':
      return 'That email looks invalid. Double-check it and try again.';
    case 'role_not_found':
      return 'That role is no longer available. Reload and pick another.';
    default:
      return 'We can’t send this invitation. Please review the fields and try again.';
  }
}

export function UsersWizardPage(): React.ReactElement {
  const navigate = useNavigate();
  const roles = useRolesList();
  const createInvite = useCreateInvitation();

  const [step, setStep] = React.useState(0);
  const [identify, setIdentify] = React.useState<IdentifyForm>(EMPTY_IDENTIFY);
  const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const identifyValid =
    identify.firstName.trim().length > 0 &&
    identify.lastName.trim().length > 0 &&
    EMAIL_RE.test(identify.email);
  const rolesValid = selectedRoleId !== null;

  const canAdvance =
    (step === 0 && identifyValid) ||
    (step === 1 && rolesValid) ||
    step === 2;

  function patchIdentify<K extends keyof IdentifyForm>(
    key: K,
    value: IdentifyForm[K],
  ): void {
    setIdentify((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(): Promise<void> {
    if (selectedRoleId === null) return;
    setErrorMessage(null);
    try {
      await createInvite.mutateAsync({
        email: identify.email,
        firstName: identify.firstName,
        lastName: identify.lastName,
        roleId: selectedRoleId,
      });
      navigate('/users');
    } catch (e: unknown) {
      const err = e as { kind?: string; reason?: string };
      if (err.kind === 'unprocessable') {
        setErrorMessage(unprocessableMessage(err.reason ?? ''));
      } else if (err.kind === 'forbidden') {
        setErrorMessage(
          'You don’t have permission to send invitations in this organization.',
        );
      } else if (err.kind === 'notFound') {
        setErrorMessage('The organization could not be found. Reload and try again.');
      } else {
        setErrorMessage('We couldn’t send the invitation. Try again in a moment.');
      }
    }
  }

  const forbiddenView = (
    <section
      aria-labelledby="invite-wizard-heading"
      className="flex flex-col gap-4 max-w-3xl"
    >
      <header className="flex flex-col gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
          Invite a teammate
        </span>
        <h1
          id="invite-wizard-heading"
          className="text-2xl font-semibold tracking-tight text-[var(--fg-primary)]"
        >
          Invite a user
        </h1>
      </header>
      <div
        role="alert"
        className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-sm text-[var(--fg-secondary)]"
      >
        You don’t have permission to send invitations in this organization.
        Ask an organization owner or admin if you need access.
      </div>
    </section>
  );

  return (
    <RequirePermission slug="identity.invitations.create" fallback={forbiddenView}>
    <section
      aria-labelledby="invite-wizard-heading"
      className="flex flex-col gap-4 max-w-3xl"
    >
      <header className="flex flex-col gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--fg-tertiary)]">
          Invite a teammate
        </span>
        <h1
          id="invite-wizard-heading"
          className="text-2xl font-semibold tracking-tight text-[var(--fg-primary)]"
        >
          Invite a user
        </h1>
        <p className="max-w-2xl text-sm text-[var(--fg-secondary)]">
          Capture who they are, the role they should hold, then review. The
          invitation email is sent immediately on submit.
        </p>
      </header>

      <WizardStepRail
        steps={STEPS}
        current={step}
        aria-label="Invite teammate steps"
      />

      <SectionCard>
        {step === 0 ? (
          <fieldset className="flex flex-col gap-4">
            <legend className="sr-only">Identify</legend>
            <SectionHeading id="invite-identify-heading">Identify</SectionHeading>
            <p className="text-sm text-[var(--fg-secondary)]">
              Their name and where to send the invitation.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label="First name"
                value={identify.firstName}
                onChange={(e) => patchIdentify('firstName', e.target.value)}
                autoComplete="given-name"
                required
              />
              <TextField
                label="Last name"
                value={identify.lastName}
                onChange={(e) => patchIdentify('lastName', e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
            <TextField
              label="Work email"
              type="email"
              value={identify.email}
              onChange={(e) => patchIdentify('email', e.target.value)}
              autoComplete="email"
              hint="We'll send a one-time invitation link."
              required
            />
            <TextField
              label="Job title (optional)"
              value={identify.jobTitle}
              onChange={(e) => patchIdentify('jobTitle', e.target.value)}
            />
          </fieldset>
        ) : null}

        {step === 1 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="sr-only">Roles</legend>
            <SectionHeading id="invite-roles-heading">Role</SectionHeading>
            <p className="text-sm text-[var(--fg-secondary)]">
              Pick the role they should hold in this organization.
            </p>
            {roles.status === 'loading' ? (
              <p
                aria-busy="true"
                className="text-sm text-[var(--fg-tertiary)]"
              >
                Loading roles…
              </p>
            ) : null}
            {roles.status === 'forbidden' ? (
              <p role="alert" className="text-sm text-[var(--fg-secondary)]">
                You don’t have permission to list roles in this organization.
              </p>
            ) : null}
            {roles.status === 'error' ? (
              <div
                role="alert"
                className="flex flex-col gap-2 text-sm text-[var(--fg-secondary)]"
              >
                <p>We couldn’t load the roles list.</p>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => roles.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : null}
            {roles.status === 'ready' ? (
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {roles.data.map((r) => {
                  const checked = selectedRoleId === r.id;
                  const radioId = `invite-role-${r.id}`;
                  return (
                    <li key={r.id}>
                      <label
                        htmlFor={radioId}
                        className={[
                          'flex items-start gap-3 cursor-pointer rounded-xl border p-4',
                          checked
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-[var(--border-default)] bg-[var(--bg-surface)]',
                        ].join(' ')}
                      >
                        <input
                          id={radioId}
                          type="radio"
                          name="invite-role"
                          checked={checked}
                          onChange={() => setSelectedRoleId(r.id)}
                          className="mt-1"
                        />
                        <span className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-[var(--fg-primary)]">
                            {r.name}
                          </span>
                          <span className="text-xs text-[var(--fg-tertiary)] leading-snug">
                            {r.isBuiltIn ? 'Built-in role' : 'Custom role'}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </fieldset>
        ) : null}

        {step === 2 ? (
          <div className="flex flex-col gap-4">
            <SectionHeading id="invite-confirm-heading">Confirm</SectionHeading>
            <p className="text-sm text-[var(--fg-secondary)]">
              Review before sending.
            </p>
            {errorMessage !== null ? (
              <div
                role="alert"
                className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 text-sm text-[var(--fg-secondary)]"
              >
                {errorMessage}
              </div>
            ) : null}
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[var(--fg-tertiary)]">Name</dt>
                <dd className="text-[var(--fg-primary)]">
                  {`${identify.firstName} ${identify.lastName}`.trim() || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--fg-tertiary)]">Email</dt>
                <dd className="text-[var(--fg-primary)]">
                  {identify.email || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--fg-tertiary)]">Job title</dt>
                <dd className="text-[var(--fg-primary)]">
                  {identify.jobTitle || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--fg-tertiary)]">Role</dt>
                <dd className="text-[var(--fg-primary)]">
                  {selectedRoleId === null
                    ? '—'
                    : roles.status === 'ready'
                      ? (roles.data.find((r) => r.id === selectedRoleId)?.name ?? selectedRoleId)
                      : selectedRoleId}
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
      </SectionCard>

      <div className="flex flex-wrap gap-2 justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/users')}
        >
          Cancel
        </Button>
        <div className="flex gap-2">
          {step > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              Back
            </Button>
          ) : null}
          {step < STEPS.length - 1 ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canAdvance}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={
                selectedRoleId === null ||
                !identifyValid ||
                createInvite.isPending
              }
              onClick={() => {
                void submit();
              }}
            >
              {createInvite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          )}
        </div>
      </div>
    </section>
    </RequirePermission>
  );
}
